import { SessionService } from '@app/shared/services/session.service';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ItemRequest, ItemsService, ProductsService, ProductResponse, CompanyResponse, ItemResponse } from '@src/app/shared/backend/clients';
import { firstValueFrom } from 'rxjs';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { ComboData } from '@src/app/shared/components/comboData/comboData';

@Component({
    selector: 'app-add-digital-card',
    templateUrl: './add-digital-card.component.html',
    styleUrl: './add-digital-card.component.scss',
    host: {
        class: 'app-add-digital-card'
    },
    standalone: false
})
export class AddDigitalCardComponent implements OnInit {

  company: CompanyResponse | null = null;
  companyIsPrepaired = false;

  showCreateSerialNumberModal: boolean = false;
  hasSubmittedSerialNumberSearch: boolean = false;

  serialNumberError: string | null = null;
  itemsList: ItemResponse[];
  isResultsEmpty: boolean = true;
  itemsRowsPerPageOption: number = 100;

  product: ProductResponse = {};

  comboData: ComboData = new ComboData(async (filter) => await firstValueFrom(this.productsService.apiProductsFilterComboPost(filter)));

  async onLazyLoadComboData(event: any) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));

    await this.comboData.getData(search, activePage);
  }

  getComboTotalPages(): number {
    const totalRecords = this.comboData?.totalRecords ?? 0;
    const pageSize = Number(this.comboData?.rowsPerPageOption) || 5;

    return Math.max(1, Math.ceil(totalRecords / pageSize));
  }

  private async initComboData() {
    await this.comboData.getData(null, 0);
  }

  public digitalCardFormGroup: FormGroup = this.fb.group({
    serialNumber: [null, [Validators.required]]
  });

  public createSerialNumberForm: FormGroup = this.fb.group({
    serialNumber: [{ value: '', disabled: true }],
    productId: [[], [Validators.required]],
    warrantyCode: ['']
  });

  filterOperator = FilterOperator;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private session: SessionService,
    private productsService: ProductsService,
    private itemsService: ItemsService) { }

  async ngOnInit() {
    await this.initComboData();

    // Get company data and set validators
    this.session.currentCompanyData$.subscribe(company => {
      this.company = company;
      this.companyIsPrepaired = !!company?.isPrepaired;

      const warrantyCodeControl = this.createSerialNumberForm.get('warrantyCode');
      if (this.companyIsPrepaired) {
        warrantyCodeControl?.setValidators([Validators.required]);
      } else {
        warrantyCodeControl?.clearValidators();
      }
      warrantyCodeControl?.updateValueAndValidity();
    });

    this.createSerialNumberForm.get('productId')?.valueChanges.subscribe(async (value) => {
      const selectedId = value?.[0];

      if (!selectedId) {
        this.product = {};
        return;
      }

      this.product = await firstValueFrom(this.productsService.apiProductsIdGet(selectedId));
    });
  }

  async searchSerialNumber() {
    this.hasSubmittedSerialNumberSearch = false;

    if (this.digitalCardFormGroup.valid) {
      this.hasSubmittedSerialNumberSearch = true;
      await this.validateSerialNumber(this.digitalCardFormGroup.controls['serialNumber'].value);
    }
  }

  qrCodeScanned(data: any) {
    this.digitalCardFormGroup.controls['serialNumber'].setValue(data, { emitEvent: false });
  }

  async validateSerialNumber(serialNumber: string): Promise<void> {
    const filters = [{
      propertyName: 'serialNumber',
      operator: this.filterOperator.EQUALS,
      values: [serialNumber]
    },
    {
      propertyName: 'Disposition',
      operator: this.filterOperator.EQUALS,
      values: ['SOLD']
    }];

    const filter = {
      pageNumber: 1,
      pageSize: this.itemsRowsPerPageOption,
      filters
    };

    try {
      const itemsPage = await firstValueFrom(this.itemsService.apiItemsFilterPost(filter));
      this.itemsList = itemsPage.results?.filter(x => x.warranties?.some(w => w.isActive)) || [];
      this.isResultsEmpty = this.itemsList.length == 0;
    } catch (error) {
      console.error('Error validating serial number', error);
    }
  }

  getCollectionNames(item: any) {
    return item?.product?.collections?.map((collection: any) => collection.name).join(', ') || '';
  }

  getCategoryNames(item: any) {
    return item?.product?.productType?.categories?.map((category: any) => category.name).join(', ') || '';
  }

  onOpenCreateSerialNumberModalbox() {
    const serialNumber = this.digitalCardFormGroup.get('serialNumber')?.value;
    this.createSerialNumberForm.patchValue({ serialNumber });
    this.digitalCardFormGroup.patchValue({ serialNumber });
    this.product = {};
    this.showCreateSerialNumberModal = true;
  }

  onCancelCreateSerialNumber() {
    this.showCreateSerialNumberModal = false;
    this.createSerialNumberForm.reset();
    this.createSerialNumberForm.patchValue({ productId: [] });
    this.product = {};
  }

  async onCreateSerialNumber() {
    if (this.createSerialNumberForm.valid) {

      const payload: ItemRequest = {
        serialNumber: this.createSerialNumberForm.controls['serialNumber'].value,
        productId: this.createSerialNumberForm.controls['productId'].value?.[0],
      };

      const itemId = await firstValueFrom(this.itemsService.apiItemsCreatePost(payload));
      this.showCreateSerialNumberModal = false;
      this.router.navigate(['/digital-card', itemId]);
    }
  }

  createDigitalCard(itemId: number) {
    this.router.navigate(['/digital-card', itemId]);
  }
}
